import { useEffect, useState, forwardRef, useCallback, useRef } from 'react'
import { Autocomplete, Checkbox, FormControl, InputLabel, TextField } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { Box } from '@mui/system'
import { VariableSizeList } from 'react-window'
import { isNotEmpty } from '../../utils/helpers'
import { ReactComponent as TeamSnap } from '../../images/TeamSnap.svg'
import UseTranslation from '../../hooks/UseTranslation'
import useIosDropdownPlacement from '../../hooks/useIosDropdownPlacement'

const Row = ({
  index, data, setSize,
}) => {
  const root = useRef();

  useEffect(() => {
    setSize(index, root.current.getBoundingClientRect().height);
  }, []);

  return (
    <Box ref={root}>
      {data}
    </Box>
  )
}

const ListboxComponent = forwardRef((props, ref) => {
  // Virtualised: height is a prop on VariableSizeList, not something CSS caps.
  const { children, maxListHeight, ...other } = props;
  const itemCount = Array.isArray(children) ? children.length : 0;
  const sizeMap = useRef({});
  const listRef = useRef(null)
  const getSize = useCallback((index) => sizeMap.current[index] || 50, []);
  const setSize = useCallback((index, size) => {
    listRef.current?.resetAfterIndex(0)

    sizeMap.current = { ...sizeMap.current, [index]: size };
  }, []);

  return (
    <Box ref={ref} {...other}>
      <VariableSizeList
        height={maxListHeight || 250}
        width="100%"
        itemSize={getSize}
        itemCount={itemCount}
        ref={listRef}
      >

        {({ index, style }) => (
          <div style={style}>
            <Row index={index} data={children[index]} setSize={setSize} />
          </div>
        )}
      </VariableSizeList>
    </Box>

  );
});

const OfoqAutoComplete = ({
  name,
  optionsConfig,
  defaultOption,
  refState,
  nameConverter,
  required,
  multiple,
  customStyle,
  customPlaceholder,
  ...props
}) => {
  const splitName = name?.split('_').join(' ')
  const [optionsArr, setOptionsArr] = useState(optionsConfig || [])
  const [isLoading, setIsLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)
  const iosPlacement = useIosDropdownPlacement(anchorRef, open)

  const handleAutocompleteOpen = useCallback(() => {
    setOpen(true)
  }, [])

  const handleAutocompleteClose = useCallback(() => {
    setOpen(false)
  }, [])

  // `null` for single, `[]` for multiple; the wrong one eats typed text on blur.
  const emptyValue = multiple ? [] : null
  const { t, language } = UseTranslation()
  useEffect(() => {
    if (optionsConfig?.length > 0) {
      setOptionsArr(optionsConfig)
      setIsLoading(false)
    } else {
      setIsLoading(false)
      setOptionsArr([])
    }

    if (isNotEmpty(optionsConfig)) {
      setIsLoading(false)
    }
  }, [optionsConfig])

  const sendSelectedItem = (value) => {
    refState(value)
  }

  const icon = <CheckBoxOutlineBlankIcon fontSize='small' />
  const checkedIcon = <CheckBoxIcon fontSize='small' />
  return (
    <FormControl ref={anchorRef} sx={[styles.formControlGrow, customStyle]}>
      <InputLabel
        htmlFor={name}
        sx={styles.formInputLabel}>
        {splitName}
            {required && <TeamSnap style={styles.snapIcon} data-testid="teamsnap-icon" />}
      </InputLabel>

      <Autocomplete
        id={name}
        limitTags={1}
        defaultValue={isNotEmpty(defaultOption) ? defaultOption : emptyValue}
        value={isNotEmpty(defaultOption) ? defaultOption : emptyValue}
        sx={styles.multiAutoComplete}
        options={optionsArr || []}
        getOptionLabel={(optionList) => optionList.name ||
          optionList.english_name ||
          (nameConverter && nameConverter(optionList)) ||
          ''}
        onChange={(_, newValue) => sendSelectedItem(newValue)}
        multiple={multiple}
        loading={isLoading}
        loadingText={t?.Loading}
        noOptionsText={language === 'EN' ? `${splitName} Not Found` : `${t?.['Not Found']} ${splitName}`}
        isOptionEqualToValue={(option, value) => (option.id === value.id)}
        renderOption={(prop, option, { selected }) => (
          <Box
            component="li"
            {...prop}
            sx={styles.option} >
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              sx={{ marginRight: { xs: '4px', sm: '8px' } }}
              checked={selected}
            />
            {nameConverter ? nameConverter(option) : option.name || option.english_name}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            sx={styles.formInput}
            className='form-input'
            placeholder={customPlaceholder || `${t?.Select} ${splitName}`}
            variant='outlined'
            fullWidth
            margin='dense'
            {...params}
          />
        )}
        ListboxComponent={ListboxComponent}
        ListboxProps={{ maxListHeight: iosPlacement.maxHeight }}
        clearOnBlur={!iosPlacement.keepTypedText}
        open={open}
        onOpen={handleAutocompleteOpen}
        onClose={handleAutocompleteClose}
        slotProps={{
          // Upwards on iOS, clear of the keyboard; capped by the hook.
          popper: iosPlacement.upwards ? iosPlacement.popperProps : undefined,
          // MUI focuses the input on a click anywhere in the Autocomplete, so
          // tapping the arrow opened the keyboard. Stop the click reaching that
          // handler and drive the toggle here instead, as the other dropdowns do.
          popupIndicator: {
            onMouseDown: (e) => {
              e.preventDefault()
            },
            onClick: (e) => {
              e.stopPropagation()
              if (open) {
                handleAutocompleteClose(e, 'toggleInput')
              } else {
                handleAutocompleteOpen(e)
              }
            },
          },
        }}
        {...props}
      />
    </FormControl>
  )
}

const styles = {
  formControlGrow: {
    width: '100%',
    marginBottom: { xs: '0px', sm: '20px' },
    '& .MuiInputLabel-outlined.MuiInputLabel-marginDense': {
      transform: 'translate(14px,12px) scale(1)',
    },
    '& .MuiFormControl-marginDense': {
      marginTop: { xs: '5px', sm: '20px' },
      marginBottom: '0',
      paddingRight: '0',
    },
    '& .MuiOutlinedInput-root': {
      marginTop: { xs: '10px', sm: 'unset' },
      borderRadius: 4,
      backgroundColor: '#fafbfc',
      height: { xs: '40px', sm: 'unset' },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#29cc8b',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'solid 2px #e2e8ee',
      transition: 'all 0.3s ease-in-out',
    },
    '& .MuiOutlinedInput-inputMarginDense': {
      padding: { xs: '8px', sm: '12px' },
    },
    '& .MuiAlert-root': {
      width: '100%',
      marginBottom: { xs: 12, sm: 16 },
    },
    '& .MuiOutlinedInput-input': {
      fontSize: { xs: '12px', sm: '14px' },
    },
  },

  formInputLabel: {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'capitalize',
    transform: 'translate(0,0) scale(1)',
    color: 'text.primary',
    fontSize: '14px',
  },
  snapIcon: {
    marginInlineStart: '8px',
  },
  multiAutoComplete: {
    margin: '10px 0',
    '& .MuiChip-label': {
      fontSize: { xs: '10px', sm: '0.875rem' },
      paddingLeft: { xs: '4px', sm: '8px' },
      paddingRight: { xs: '4px', sm: '8px' },
      marginTop: { xs: '0px', sm: '5px' },
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      height: { xs: '21px', sm: '25px' },
      color: '#000',
    },

    '& .MuiChip-deleteIcon': {
      color: '#29cc8b',
      fill: '#29cc8b',
      width: '18px',
      margin: '0px 2px 0px -2px',
      cursor: 'pointer',
    },

    '& .MuiSvgIcon-root': {
      fill: 'currentColor',
      display: 'inline-block',
      fontSize: { xs: '1rem', sm: '1.5rem' },
      transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      flexShrink: 0,
      userSelect: 'none',
    },

    '& .MuiAutocomplete-tag': {
      paddingRight: '5px',
      paddingBottom: '3px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '10px',
      border: 'solid 1px rgba(41, 204, 139, 0.59)',
      gap: { xs: '4px', sm: 'unset' },
      backgroundColor: '#edfff8',
      '& .MuiChip-deleteIcon': { fill: '#29cc8b' },
    },
  },
  formInput: {
    height: { xs: '40px', sm: 'unset' },
  },
  option: {
    fontSize: { xs: '14px', sm: '16px' },
  },
}

export default OfoqAutoComplete
